<script setup>
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import { VueMonacoEditor } from '@guolao/vue-monaco-editor';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  template: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:visible', 'save']);

// Estados locales de edición
const editContent = ref('');
const editScript = ref('');
const editControls = ref('');

// Watch para cargar datos cuando se abre el diálogo
watch(() => props.template, (newTemplate) => {
  if (newTemplate) {
    editContent.value = newTemplate.content || '';
    editScript.value = newTemplate.script || '';
    editControls.value = JSON.stringify(newTemplate.controls || {}, null, 2);
  }
}, { immediate: true });

// Opciones para los editores
const htmlOptions = {
  theme: 'vs-dark',
  minimap: { enabled: false },
  fontSize: 14,
  lineNumbers: 'on',
  automaticLayout: true,
  scrollBeyondLastLine: false,
  wordWrap: 'on'
};

const jsOptions = {
  ...htmlOptions,
  language: 'javascript'
};

const jsonOptions = {
  ...htmlOptions,
  language: 'json'
};

// Funciones de botones
const handleClose = () => {
  emit('update:visible', false);
};

const handleSave = () => {
  try {
    // Validar JSON antes de guardar
    const controlsObj = JSON.parse(editControls.value);
    
    emit('save', {
      content: editContent.value,
      script: editScript.value,
      controls: controlsObj
    });
    
    console.log('✅ Cambios guardados en memoria');
  } catch (error) {
    alert('Error en el JSON de controls: ' + error.message);
    console.error('❌ Error al parsear controls:', error);
  }
};

const handleSaveAndClose = () => {
  handleSave();
  handleClose();
};
</script>

<template>
  <Dialog 
    :visible="visible" 
    @update:visible="emit('update:visible', $event)"
    modal 
    :closable="false"
    :style="{ width: '95vw', height: '95vh' }"
    :contentStyle="{ height: 'calc(95vh - 120px)', padding: 0 }">
    
    <template #header>
      <div class="flex align-items-center gap-2">
        <span class="font-bold text-xl">Editor de Plantilla</span>
        <span v-if="template" class="text-sm opacity-70">{{ template.name }} ({{ template.id }})</span>
      </div>
    </template>

    <!-- Contenido del editor -->
    <div class="flex h-full">
      <!-- Columna izquierda: HTML Content -->
      <div class="flex-1 flex flex-column border-right-1 surface-border">
        <div class="bg-gray-800 text-white px-3 py-2 text-sm font-semibold">
          HTML Content
        </div>
        <div class="flex-1">
          <VueMonacoEditor
            v-model:value="editContent"
            language="html"
            :options="htmlOptions"
            style="height: 100%;"
          />
        </div>
      </div>

      <!-- Columna derecha: Script y Controls -->
      <div class="flex-1 flex flex-column">
        <!-- Script (arriba) -->
        <div class="flex-1 flex flex-column border-bottom-1 surface-border">
          <div class="bg-gray-800 text-white px-3 py-2 text-sm font-semibold">
            JavaScript Script
          </div>
          <div class="flex-1">
            <VueMonacoEditor
              v-model:value="editScript"
              language="javascript"
              :options="jsOptions"
              style="height: 100%;"
            />
          </div>
        </div>

        <!-- Controls (abajo) -->
        <div class="flex-1 flex flex-column">
          <div class="bg-gray-800 text-white px-3 py-2 text-sm font-semibold">
            Controls (JSON)
          </div>
          <div class="flex-1">
            <VueMonacoEditor
              v-model:value="editControls"
              language="json"
              :options="jsonOptions"
              style="height: 100%;"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Botones al pie -->
    <template #footer>
      <div class="flex gap-2 justify-content-end">
        <button 
          @click="handleClose"
          class="px-4 py-2 border-round cursor-pointer text-sm font-semibold transition-colors"
          style="background: #6c757d; color: white; border: none;"
        >
          Cerrar sin Guardar
        </button>
        <button 
          @click="handleSave"
          class="px-4 py-2 border-round cursor-pointer text-sm font-semibold transition-colors"
          style="background: #0d6efd; color: white; border: none;"
        >
          Guardar
        </button>
        <button 
          @click="handleSaveAndClose"
          class="px-4 py-2 border-round cursor-pointer text-sm font-semibold transition-colors"
          style="background: #198754; color: white; border: none;"
        >
          Guardar y Cerrar
        </button>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
:deep(.p-dialog-content) {
  overflow: hidden;
}
</style>
